import {announce} from '@github-ui/aria-live'
import {useCallback, useEffect, useRef, useState, type RefObject} from 'react'

interface ManagedDefaultModelSelection {
  selectedModelId: string | undefined
  selectModel: (modelId: string) => void
  isSelectionReady: boolean
  isUsingManagedDefault: boolean
}

export function useModelSelectionLoadingAnnouncement(
  isAhpEnvironment: boolean,
  isSelectionReady: boolean,
  modelsError: Error | null,
  elementRef: RefObject<HTMLElement | null>,
) {
  const hasAnnouncedLoadingRef = useRef(false)
  const wasLoadingRef = useRef(false)

  useEffect(() => {
    if (isAhpEnvironment) {
      hasAnnouncedLoadingRef.current = false
      wasLoadingRef.current = false
      return
    }

    if (!isSelectionReady) {
      wasLoadingRef.current = true
      if (!hasAnnouncedLoadingRef.current) {
        const announcementTimeout = setTimeout(() => {
          announce('Preparing model selection…', {element: elementRef.current ?? undefined})
          hasAnnouncedLoadingRef.current = true
        }, 300)
        return () => clearTimeout(announcementTimeout)
      }
      return
    }

    if (modelsError && wasLoadingRef.current) {
      announce('Model options could not be loaded.', {element: elementRef.current ?? undefined})
    } else if (hasAnnouncedLoadingRef.current) {
      announce('Model selection ready.', {element: elementRef.current ?? undefined})
    }
    hasAnnouncedLoadingRef.current = false
    wasLoadingRef.current = false
  }, [elementRef, isAhpEnvironment, isSelectionReady, modelsError])
}

/**
 * Applies the managed default as the session-only initial selection for each agent scope.
 * User selections still persist normally; the managed default never overwrites localStorage.
 */
export function useManagedDefaultModel(
  managedDefaultModelId: string | undefined,
  isLoading: boolean,
  scopeKey: string,
  persistedModelId: string | undefined,
  selectModel: (modelId: string) => void,
): ManagedDefaultModelSelection {
  const isSelectionReady = !isLoading

  // Per-scope in-memory selection for this mount. Seeded from the managed default (if
  // any) and then updated by user picks; never read back from localStorage.
  const [sessionModelByScope, setSessionModelByScope] = useState<Record<string, string>>({})
  // Scopes already seeded this mount, so each scope seeds at most once.
  const [seededScopes, setSeededScopes] = useState<Record<string, true>>({})

  // Seed the current scope during render once models have loaded. The per-scope guard
  // flips in the same pass, so this runs at most once per scope and cannot loop. Setting
  // state during render cancels the in-progress render and restarts it before commit, so
  // the persisted value is never committed.
  if (managedDefaultModelId && !isLoading && !seededScopes[scopeKey]) {
    setSeededScopes(prev => ({...prev, [scopeKey]: true}))
    setSessionModelByScope(prev => ({...prev, [scopeKey]: managedDefaultModelId}))
  }

  const handleSelectModel = useCallback(
    (modelId: string) => {
      // Record the pick in-memory for this scope so it wins for the rest of the mount,
      // then delegate to the underlying setter (which persists to localStorage for the
      // scope and clears any unsupported reasoning effort).
      if (managedDefaultModelId) {
        setSessionModelByScope(prev => ({...prev, [scopeKey]: modelId}))
      }
      selectModel(modelId)
    },
    [managedDefaultModelId, scopeKey, selectModel],
  )

  if (!managedDefaultModelId) {
    return {selectedModelId: persistedModelId, selectModel, isSelectionReady, isUsingManagedDefault: false}
  }

  const selectedModelId = sessionModelByScope[scopeKey] ?? persistedModelId
  return {
    selectedModelId,
    selectModel: handleSelectModel,
    isSelectionReady,
    isUsingManagedDefault: selectedModelId === managedDefaultModelId,
  }
}
